import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/api';
import { getAssetDownloadUrl } from './assetUrlUtils';

/**
 * Character Studio storage service.
 *
 * Character images are persisted in the logged-in user's own Azure Blob
 * directory (`users/<userId>/assets/...`), NOT under a video id. The frontend
 * requests a signed upload URI via `getUploadURI`, PUTs the raw image blob to
 * it, and stores the returned relative blob path on the character document.
 *
 * To render those private blobs the images are re-signed with the user-level
 * SAS token that is returned alongside the user profile at login and cached
 * against the user id in app storage (localStorage).
 */

interface GenerateUploadUriResponse {
  uploadUrl: string;
  location: string;
}

export interface UserSasEntry {
  token: string;
  expiredAt: number;
}

const USER_SAS_STORAGE_PREFIX = 'pixle_user_sas_';
// Lead time before the SAS token expires at which we refresh it (mirrors the
// video-level SAS buffer used in `assetUrlUtils`).
const DEFAULT_SAS_REFRESH_BUFFER_MS = 5 * 60 * 1000;

function getCurrentUserId(): string | null {
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return (
      storedUser?._id ||
      storedUser?.id ||
      localStorage.getItem('userId') ||
      null
    );
  } catch {
    return localStorage.getItem('userId');
  }
}

const getUserSasStorageKey = (userId: string): string =>
  `${USER_SAS_STORAGE_PREFIX}${userId}`;

function normalizeSas(
  sas?: { token?: string; expiredAt?: number } | null
): UserSasEntry | null {
  if (!sas?.token) return null;

  return {
    token: sas.token,
    expiredAt:
      typeof sas.expiredAt === 'number'
        ? sas.expiredAt
        : Date.now() + 3600 * 1000,
  };
}


/**
 * Request a signed upload URI for a character image.
 *
 * No video id is passed, so the backend places the blob in the user's own
 * directory: `users/<userId>/assets/<fileName>`.
 */
export const getUploadURI = async (
  fileName: string,
  token: string
): Promise<GenerateUploadUriResponse> => {
  const url = `${API_BASE_URL}/storage/generateUploadUrl?fileName=${encodeURIComponent(
    fileName
  )}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to generate upload URL: ${response.statusText}`);
  }

  return await response.json();
};

/** Upload the character image blob to the signed URI returned by getUploadURI. */
export const uploadCharacterImage = async (
  blob: Blob,
  uploadUrl: string
): Promise<boolean> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': blob.type || 'image/png',
    },
    body: blob,
  });

  if (!response.ok) {
    throw new Error(`Failed to upload character image: ${response.statusText}`);
  }

  return true;
};

/**
 * Maximum allowed length (seconds) of a character voice reference clip.
 * Clips are used as a short "how this character sounds" reference, so the UI
 * rejects anything longer and surfaces a reminder to keep it to 15 seconds.
 */
export const MAX_VOICE_REFERENCE_SECONDS = 15;

/**
 * Minimum usable length (seconds) of a character voice reference clip.
 * The video model rejects audio references shorter than 2 seconds, so the
 * UI refuses shorter clips up front instead of failing at generation time.
 * Set to 2.5s (not 2.0s) as a safety margin: lossy metadata can probe a
 * true 2-second file as slightly shorter on either end.
 */
export const MIN_VOICE_REFERENCE_SECONDS = 2.5;

/** Audio extensions accepted for a voice reference (mirrors the upload UI). */
const SUPPORTED_VOICE_EXTENSIONS = [
  'mp3',
  'wav',
  'ogg',
  'oga',
  'm4a',
  'aac',
  'flac',
  'opus',
  'weba',
  'aiff',
  'aif',
  'm4b',
];

/** Content-Type fallbacks used when the browser reports an empty file.type. */
const VOICE_EXTENSION_CONTENT_TYPES: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  m4a: 'audio/mp4',
  m4b: 'audio/mp4',
  aac: 'audio/aac',
  flac: 'audio/flac',
  opus: 'audio/opus',
  weba: 'audio/webm',
  aiff: 'audio/x-aiff',
  aif: 'audio/x-aiff',
};

/** `accept` value for a character voice reference file picker. */
export const VOICE_REFERENCE_ACCEPT =
  'audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/aac,audio/flac,audio/webm,.mp3,.wav,.ogg,.oga,.m4a,.aac,.flac,.opus,.weba,.aiff,.aif,.m4b';

const getFileExtension = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() || '';

/**
 * Whether the selected file can be used as a character voice reference.
 * Accepts any `audio/*` MIME type and the common audio extensions (covers the
 * case where a browser reports an empty or non-standard type).
 */
export const isSupportedVoiceReferenceFile = (file: File): boolean => {
  if (file.type.toLowerCase().startsWith('audio/')) return true;
  return SUPPORTED_VOICE_EXTENSIONS.includes(getFileExtension(file.name));
};

/**
 * Probe a voice reference clip's duration in seconds by loading only its
 * metadata. Accepts a File/Blob (read through a temporary object URL) or a
 * playable URL. Used to enforce the 15-second voice reference limit.
 */
export const probeAudioDuration = (source: Blob | string): Promise<number> =>
  new Promise((resolve, reject) => {
    const element = new Audio();
    const objectUrl =
      typeof source === 'string' ? null : URL.createObjectURL(source);
    const src = typeof source === 'string' ? source : (objectUrl as string);

    const cleanup = () => {
      element.removeEventListener('loadedmetadata', handleLoadedMetadata);
      element.removeEventListener('error', handleError);
      element.src = '';
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    const handleLoadedMetadata = () => {
      const duration = Number.isFinite(element.duration) ? element.duration : 0;
      cleanup();
      resolve(duration);
    };
    const handleError = () => {
      cleanup();
      reject(new Error('Failed to load audio metadata'));
    };

    element.preload = 'metadata';
    element.addEventListener('loadedmetadata', handleLoadedMetadata);
    element.addEventListener('error', handleError);
    element.src = src;
    element.load();
  });

/**
 * Upload a character voice reference clip to base storage.
 *
 * No video id is passed, so the backend places the blob in the user's own
 * directory (`users/<userId>/assets/<fileName>`) — the same "base" storage
 * used for character images. Returns the relative blob location, which the
 * caller stores on the character document as the flat `voiceUrl` field.
 */
export const uploadCharacterVoiceReference = async (
  file: File,
  token: string
): Promise<string> => {
  const { uploadUrl, location } = await getUploadURI(file.name, token);

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type':
        file.type ||
        VOICE_EXTENSION_CONTENT_TYPES[getFileExtension(file.name)] ||
        'audio/mpeg',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to upload voice reference: ${response.statusText}`
    );
  }

  return location;
};

/** Persist the current user's SAS token keyed by user id in app storage. */
export const cacheUserSas = (entry: UserSasEntry): void => {
  const userId = getCurrentUserId();
  if (!userId) return;

  try {
    localStorage.setItem(getUserSasStorageKey(userId), JSON.stringify(entry));
  } catch (error) {
    console.error('Failed to cache user SAS token:', error);
  }
};

/** Read the cached SAS entry for the current user (if any). */
export const getCachedUserSas = (): UserSasEntry | null => {
  const userId = getCurrentUserId();
  if (!userId) return null;

  try {
    const raw = localStorage.getItem(getUserSasStorageKey(userId));
    if (!raw) return null;

    const entry = JSON.parse(raw) as UserSasEntry;
    if (!entry?.token) return null;

    return entry;
  } catch {
    return null;
  }
};

/**
 * Fetch the SAS token the backend returns with the user profile
 * (`/auth/profile` -> `user.sas`) and cache it against the user id. Intended
 * to be called right after login so the SAS is available offline thereafter.
 */
export const fetchAndCacheUserSasFromProfile = async (
  token: string
): Promise<UserSasEntry | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) return null;

    const data = await response.json();
    const entry = normalizeSas(data?.user?.sas);
    if (entry) cacheUserSas(entry);

    return entry;
  } catch (error) {
    console.error('Failed to fetch user SAS from profile:', error);
    return null;
  }
};

/** Fetch a fresh user-level SAS token from the backend and cache it. */
export const refreshUserSas = async (token: string): Promise<UserSasEntry> => {
  const response = await fetch(`${API_BASE_URL}/storage/getSasToken`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch SAS token: ${response.statusText}`);
  }

  const data = await response.json();

  const entry = normalizeSas(data?.sas);
  if (!entry) {
    throw new Error('SAS token response did not include a token');
  }

  cacheUserSas(entry);
  return entry;
};

/**
 * Return a usable user-level SAS token for the current user. Uses the value
 * cached against the user id in app storage and transparently refreshes it
 * from the backend when it is missing or near expiry.
 */
export const getUserSasToken = async (): Promise<string | null> => {
  const cached = getCachedUserSas();

  if (
    cached?.token &&
    Date.now() < cached.expiredAt - DEFAULT_SAS_REFRESH_BUFFER_MS
  ) {
    return cached.token;
  }

  const token = localStorage.getItem('authToken');
  if (!token) return cached?.token ?? null;

  try {
    const fresh = await refreshUserSas(token);
    return fresh.token;
  } catch (error) {
    console.error('Failed to refresh user SAS token:', error);
    // Fall back to the cached token (possibly stale) rather than nothing.
    return cached?.token ?? null;
  }
};

/**
 * Resolve a stored character image location into a playable URL.
 *
 * Relative blob paths (e.g. `users/<id>/assets/foo.png`) are signed with the
 * user-level SAS token. Direct references (data:, blob:, https://...) pass
 * through unchanged, which keeps legacy characters (stored as base64 data
 * URLs) and freshly uploaded local previews working.
 */
export const resolveCharacterImageUrl = async (
  location?: string | null
): Promise<string> => {
  if (!location) return '';

  const sasToken = await getUserSasToken();
  return getAssetDownloadUrl(location, sasToken);
};

const isDirectAssetUrl = (value?: string | null): boolean =>
  !!value && /^(https?:|blob:|data:)/i.test(value);

/**
 * React hook that resolves a stored character image location into a playable
 * signed URL for rendering (`<img src>` / canvas). Direct URLs resolve
 * synchronously; relative blob paths resolve asynchronously once the user SAS
 * token is available.
 */
export const useCharacterImageUrl = (location?: string | null): string => {
  const [url, setUrl] = useState<string>(() =>
    isDirectAssetUrl(location) ? (location as string) : ''
  );

  useEffect(() => {
    let cancelled = false;

    if (isDirectAssetUrl(location)) {
      setUrl(location as string);
      return () => {
        cancelled = true;
      };
    }

    setUrl('');
    resolveCharacterImageUrl(location)
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch(() => {
        if (!cancelled) setUrl(location || '');
      });

    return () => {
      cancelled = true;
    };
  }, [location]);

  return url;
};

/**
 * Resolve a stored character voice reference location into a playable URL.
 *
 * Relative blob paths (e.g. `users/<id>/assets/voice.mp3`) are signed with the
 * user-level SAS token. Direct references (data:, blob:, https://...) pass
 * through unchanged.
 */
export const resolveCharacterVoiceUrl = async (
  location?: string | null
): Promise<string> => {
  if (!location) return '';

  const sasToken = await getUserSasToken();
  return getAssetDownloadUrl(location, sasToken);
};

/**
 * React hook that resolves a stored character voice reference location into a
 * playable signed URL for rendering (`<audio src="...">`). Direct URLs resolve
 * synchronously; relative blob paths resolve asynchronously once the user SAS
 * token is available.
 */
export const useCharacterVoiceUrl = (location?: string | null): string => {
  const [url, setUrl] = useState<string>(() =>
    isDirectAssetUrl(location) ? (location as string) : ''
  );

  useEffect(() => {
    let cancelled = false;

    if (isDirectAssetUrl(location)) {
      setUrl(location as string);
      return () => {
        cancelled = true;
      };
    }

    setUrl('');
    resolveCharacterVoiceUrl(location)
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch(() => {
        if (!cancelled) setUrl(location || '');
      });

    return () => {
      cancelled = true;
    };
  }, [location]);

  return url;
};

